/**
 * CounterSignalChips — chip strip for a trade's in-trade counter-signals
 * (Phase 19).
 *
 * Sibling of TradeFlagChips. Where TradeFlagChips renders bar-context
 * single-event flags (sharp entry, wick stop, MFE leak, etc.), this
 * renders the in-trade counter-signals captured at backfill time by
 * `detectInTradeCounterSignals` and stored on
 * `trade.barAnalysis.counterSignals`.
 *
 * Each chip shows:
 *   - Signal name: "15m close vs 9 EMA"
 *   - Minutes into trade: "+12m"
 *   - Hover tooltip with full detail (close price, EMA value, distance)
 *
 * Rendering rules:
 *   - Hidden when no counterSignals exist on barAnalysis (silent — same
 *     as TradeFlagChips when no flags)
 *   - All counter-signals render in amber (they're warnings by definition)
 *   - Sorted by barTime ascending (earliest signal first)
 *
 * Props:
 *   trade     The trade object — reads trade.barAnalysis.counterSignals
 *   darkMode  bool
 *   compact   bool — tighter padding for dense lists
 */

import { signalLabel } from '../../services/barAnalysis/counterSignals';

const fmtMinsIntoTrade = (secs) => {
  if (!Number.isFinite(secs) || secs < 0) return '';
  const mins = Math.round(secs / 60);
  if (mins < 1) return '<1m';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const CounterSignalChips = ({ trade, darkMode = true, compact = false }) => {
  const events = Array.isArray(trade?.barAnalysis?.counterSignals)
    ? trade.barAnalysis.counterSignals
    : null;
  if (!events || events.length === 0) return null;

  const padding = compact ? 'px-1.5 py-0' : 'px-2 py-0.5';
  const text    = compact ? 'text-[10px]' : 'text-xs';
  const chipCls = darkMode
    ? 'bg-amber-900/40 text-amber-300 border-amber-700/40'
    : 'bg-amber-100 text-amber-800 border-amber-200';

  return (
    <div className="flex flex-wrap gap-1.5">
      {events.map((ev, i) => {
        const label = signalLabel(ev);
        const timing = fmtMinsIntoTrade(ev.secondsIntoTrade);
        const tooltip = `${label}${timing ? ` @ +${timing}` : ''}` +
          (ev.close != null && ev.ema != null
            ? `\nClose: ${ev.close.toFixed(2)}  ·  EMA: ${ev.ema.toFixed(2)}  ·  Distance: ${ev.distancePoints} pts${ev.distanceR != null ? ` (${ev.distanceR}R)` : ''}`
            : '');
        return (
          <span
            key={`${label}-${ev.barTime}-${i}`}
            title={tooltip}
            className={`inline-flex items-center gap-1 ${padding} ${text} font-medium rounded border ${chipCls} cursor-help`}
          >
            <span aria-hidden="true">⚠</span>
            {label}
            {timing && (
              <span className={darkMode ? 'text-amber-400/80' : 'text-amber-700/80'}>
                +{timing}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
};

export default CounterSignalChips;
